import { useState } from "react";
import { GoogleMap, MarkerF, InfoWindowF, useJsApiLoader } from "@react-google-maps/api";
import { config } from "../../services/runtimeConfig";
import { Spinner } from "../common/Spinner";
import { EmptyState } from "../common/EmptyState";

const containerStyle = { width: "100%", height: "420px", borderRadius: "16px" };
const STATUS_COLORS = {
  Active: "#2e8b57",
  Dead: "#d64545",
  Sold: "#3b82c4",
  Merged: "#8a6d3b",
  Absconded: "#e0a800",
  Archived: "#888888",
};

/** hives: [{ hive_id, hive_number, status, site_id, dock_name, latitude, longitude }]
 *  weatherOverlay: optional { lat, lon, summary } rendered as a persistent info card (Advanced) */
export function HiveMap({ hives, weatherOverlay, onMarkChecked, onLogIssue }) {
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: config.googleMapsApiKey });
  const [selected, setSelected] = useState(null);

  if (!config.googleMapsApiKey) {
    return <EmptyState icon="🗺️" title="Map unavailable" description="Google Maps API key is not configured." />;
  }

  // A key can be present and still rejected -- revoked, over quota, or
  // restricted to referrers that do not include this host
  // (RefererNotAllowedMapError, which is what took the page down on
  // 2026-08-10). Rendering into a half-initialised SDK throws on
  // window.google.maps below and the error boundary swallows the whole
  // page, so the user sees "Oops something went wrong" with no clue that
  // the cause is a key setting. Fail visibly and locally instead.
  if (loadError) {
    return (
      <EmptyState
        icon="🗺️"
        title="Map unavailable"
        description="Google Maps refused this request -- usually the API key's allowed referrers, its quota, or the key itself. The rest of this page still works; see the browser console for the exact error."
      />
    );
  }

  if (!isLoaded) return <Spinner label="Loading map" />;

  // Defensive: a caller that failed its fetch can hand us undefined, and a
  // crash here would take out the page for what is really an empty map.
  const withCoords = (hives || []).filter((h) => h.latitude && h.longitude);
  if (withCoords.length === 0) {
    return <EmptyState icon="🗺️" title="No hive locations yet" description="Add GPS coordinates to your sites to see them on the map." />;
  }

  const center = { lat: withCoords[0].latitude, lng: withCoords[0].longitude };

  return (
    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={11}>
      {withCoords.map((hive) => (
        <MarkerF
          key={hive.hive_id}
          position={{ lat: hive.latitude, lng: hive.longitude }}
          icon={{
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: STATUS_COLORS[hive.status] || "#888",
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: "#fff",
            scale: 9,
          }}
          onClick={() => setSelected(hive)}
        />
      ))}

      {selected && (
        <InfoWindowF position={{ lat: selected.latitude, lng: selected.longitude }} onCloseClick={() => setSelected(null)}>
          <div style={{ minWidth: 160 }}>
            <strong>Hive {selected.hive_number}</strong>
            <p style={{ margin: "4px 0" }}>{selected.dock_name} — {selected.status}</p>
            {(onMarkChecked || onLogIssue) && (
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                {onMarkChecked && (
                  <button onClick={() => onMarkChecked(selected.hive_id)}>Mark Checked</button>
                )}
                {onLogIssue && <button onClick={() => onLogIssue(selected.hive_id)}>Log Issue</button>}
              </div>
            )}
          </div>
        </InfoWindowF>
      )}

      {weatherOverlay && (
        <InfoWindowF position={{ lat: weatherOverlay.lat, lng: weatherOverlay.lon }}>
          <div>🌤️ {weatherOverlay.summary}</div>
        </InfoWindowF>
      )}
    </GoogleMap>
  );
}
